import { inject, Injectable } from '@angular/core';
import { CmsProvider, GsStructPage, noop } from '@gs/shared';
import { Observable, shareReplay } from 'rxjs';

/**
 * Fires the CMS page request as early as possible (from an app initializer, before the router resolves
 * the lazy page route) and caches the shared, hot stream. The page-renderer then adopts the prefetched
 * stream instead of issuing its own request, so the response — which gates FCP/LCP — arrives sooner.
 * The request goes through the regular HttpClient, so all interceptors and the SSR transfer cache apply.
 */
@Injectable({
  providedIn: 'root'
})
export class CmsPrefetchService {
  // Services
  private readonly cmsProvider: CmsProvider = inject(CmsProvider);

  // Properties & Signals
  /** Prefetched, shared CMS streams keyed by the requested url. */
  private readonly cache = new Map<string, Observable<GsStructPage>>();

  // Methods
  /**
   * Kicks off (hot) a CMS page request for the given url and caches the shared stream. No-op if a
   * prefetch for the same url is already in flight.
   * @param url The fully-qualified page url to prefetch.
   */
  prefetch(url: string): void {
    if (this.cache.has(url)) return;
    const stream = this.cmsProvider.getPageByUrl(url).pipe(shareReplay({ bufferSize: 1, refCount: false }));
    // Subscribe once to make the stream hot (fire the request now); errors are surfaced again to the
    // page-renderer when it subscribes, so they are intentionally swallowed here.
    stream.subscribe({ error: noop });
    this.cache.set(url, stream);
  }

  /**
   * Returns the prefetched stream for the url and removes it from the cache, or undefined when no
   * prefetch exists.
   * @param url The fully-qualified page url to consume.
   * @returns The shared CMS stream, or undefined.
   */
  consume(url: string): Observable<GsStructPage> | undefined {
    const stream = this.cache.get(url);
    if (stream) this.cache.delete(url);
    return stream;
  }
}
